import React from 'react';
import { FRIENDLY_BLOCK_NAMES } from '../utils/constants';
import { isIntroFrontPageEnabled, sanitizeLayoutMapping } from '../utils';

/**
 * LayoutPanel component for GuiBuilder
 * Grid layout template selector and zone mappings
 */
export default function LayoutPanel({ properties, setProperties, moveBlock }) {
    const layoutMapping = sanitizeLayoutMapping(properties.layoutMapping);
    const introEnabled = isIntroFrontPageEnabled(properties.designSettings);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
                <label style={{ 
                    fontSize: '0.8rem', 
                    fontWeight: 700, 
                    textTransform: 'uppercase', 
                    color: 'var(--text-secondary)' 
                }}>
                    Page layout
                </label>
                <select
                    className="input-field"
                    style={{ 
                        fontSize: '0.85rem', 
                        padding: '0.5rem', 
                        marginTop: '0.5rem', 
                        width: '100%' 
                    }}
                    value={properties.layoutTemplate}
                    onChange={e => setProperties({ ...properties, layoutTemplate: e.target.value })}
                >
                    <option value="split-70-30">70/30 Columns (Main / Sidebar)</option>
                    <option value="split-50-50">50/50 Columns (Equal Columns)</option>
                    <option value="split-30-70">30/70 Columns (Sidebar / Main)</option>
                    <option value="single-column">Single Column (Centered)</option>
                </select>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                <h4 style={{ 
                    fontSize: '0.8rem', 
                    fontWeight: 700, 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.05em', 
                    color: 'var(--text-secondary)', 
                    margin: '0 0 0.75rem 0' 
                }}>
                    Zone Mappings
                </h4>
                {introEnabled && (
                    <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                        Intro / welcome is a front page and is not placed in these layout zones.
                    </p>
                )}
                
                {/* Column 1 Blocks */}
                <div style={{ 
                    background: 'var(--bg-primary)', 
                    padding: '0.75rem', 
                    borderRadius: '6px', 
                    border: '1px solid var(--border)', 
                    marginBottom: '1rem' 
                }}>
                    <div style={{ 
                        fontSize: '0.75rem', 
                        fontWeight: 700, 
                        color: 'var(--text-secondary)', 
                        marginBottom: '0.5rem' 
                    }}>
                        LEFT CONTAINER / COLUMN 1
                    </div>
                    {layoutMapping.column1.length === 0 && (
                        <div style={{ 
                            fontSize: '0.75rem', 
                            fontStyle: 'italic', 
                            color: 'var(--text-secondary)', 
                            padding: '0.5rem' 
                        }}>
                            Empty container
                        </div>
                    )}
                    {layoutMapping.column1.map((block, idx) => (
                        <div key={block} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            background: 'var(--bg-secondary)', 
                            border: '1px solid var(--border)', 
                            borderRadius: '4px', 
                            padding: '0.4rem 0.6rem', 
                            margin: '0.25rem 0', 
                            fontSize: '0.75rem' 
                        }}>
                            <span>{FRIENDLY_BLOCK_NAMES[block] || block}</span>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                {idx > 0 && (
                                    <button 
                                        style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} 
                                        onClick={() => moveBlock(block, 'column1', -1)}
                                    >
                                        <i className="fa-solid fa-chevron-up"></i>
                                    </button>
                                )}
                                {idx < layoutMapping.column1.length - 1 && (
                                    <button 
                                        style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} 
                                        onClick={() => moveBlock(block, 'column1', 1)}
                                    >
                                        <i className="fa-solid fa-chevron-down"></i>
                                    </button>
                                )}
                                {properties.layoutTemplate !== 'single-column' && (
                                    <button 
                                        style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} 
                                        onClick={() => moveBlock(block, 'column2')}
                                    >
                                        <i className="fa-solid fa-chevron-right"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Column 2 Blocks */}
                {properties.layoutTemplate !== 'single-column' && (
                    <div style={{ 
                        background: 'var(--bg-primary)', 
                        padding: '0.75rem', 
                        borderRadius: '6px', 
                        border: '1px solid var(--border)' 
                    }}>
                        <div style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: 700, 
                            color: 'var(--text-secondary)', 
                            marginBottom: '0.5rem' 
                        }}>
                            RIGHT CONTAINER / COLUMN 2
                        </div>
                        {layoutMapping.column2.length === 0 && (
                            <div style={{ 
                                fontSize: '0.75rem', 
                                fontStyle: 'italic', 
                                color: 'var(--text-secondary)', 
                                padding: '0.5rem' 
                            }}>
                                Empty container
                            </div>
                        )}
                        {layoutMapping.column2.map((block, idx) => (
                            <div key={block} style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                background: 'var(--bg-secondary)', 
                                border: '1px solid var(--border)', 
                                borderRadius: '4px', 
                                padding: '0.4rem 0.6rem', 
                                margin: '0.25rem 0', 
                                fontSize: '0.75rem' 
                            }}>
                                <span>{FRIENDLY_BLOCK_NAMES[block] || block}</span>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                    {idx > 0 && (
                                        <button 
                                            style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} 
                                            onClick={() => moveBlock(block, 'column2', -1)}
                                        >
                                            <i className="fa-solid fa-chevron-up"></i>
                                        </button>
                                    )}
                                    {idx < layoutMapping.column2.length - 1 && (
                                        <button 
                                            style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} 
                                            onClick={() => moveBlock(block, 'column2', 1)}
                                        >
                                            <i className="fa-solid fa-chevron-down"></i>
                                        </button>
                                    )}
                                    <button 
                                        style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} 
                                        onClick={() => moveBlock(block, 'column1')}
                                    >
                                        <i className="fa-solid fa-chevron-left"></i>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
